import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import signOut from "../../functions/userIdentity/signOut";
import getNameFromOIDCToken from "../../functions/userIdentity/getNameFromOIDCToken";
import useOutside from "../../hooks/useOutside";
import "./nav.css";
import { importMindMap } from "../../redux/actions/TodoManipulation/importMindmapAction";
import { getTheme } from "../../functions/themeService/themeService";
import axios from "../../api/axiosConfig";
import {
    createFilterCanSeeAction,
    getfilterSetCanSeeCompletedActionType,
    getfilterSetCanSeeHiddenActionType
} from "../../redux/actions/filterAction";
import { getFilterState } from "../../functions/filterState/filterState";
import { filterOverview, filterSearchTodoList } from "../../utils/todo-utils";
import { setFilteredSearchResults } from "../../redux/actions/setSearchResultAction";
import { getTimelineTodoList } from "../../store/selector";
import { useIsMobile as useIsMobileHook } from "../../hooks/useIsMobile";
import { Toast } from "@everway/design-system-react";
import { getItem } from "../../utils/storageHelper";
import { Capacitor } from '@capacitor/core';
import { triggerCalendarSync } from "../../redux/actions/Calendar/triggerCalendarSyncAction";
import { openGlobalTasksHelp } from "../../utils/globalTasksHelp";


const NavComponent = (props) => {
    const { openSettingsModal, showHelp = false } = props;
    const [showProfileMenu, setShowProfileMenu] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [isSyncing, setIsSyncing] = useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const contextMenuRef = useRef();
    const profileButtonRef = useRef();
    const userNameRef = useRef();
    const userPhotoRef = useRef();
    const userIconRef = useRef();
    const [theme, setThemePage] = useState('light');
    const isMobile = useIsMobileHook();
    const [firebaseUser, setFirebaseUser] = useState(null);
    const [userName, setUserName] = useState(firebaseUser?.name);
    const countdown = useSelector((state) => state.license.countdown);
    const photoUrl = useSelector((state) => state.userDetails.photoUrl);
    const [showFilters, setShowFilters] = useState(false);
    const fileInputRef = useRef(null);
    const filterMenuRef = useRef();
    const feature = "Overview";
    const filterStateName = getFilterState(feature) ?? "overviewFilterState";
    const projects = useSelector((state) => state.taskList.data);
    const filterState = useSelector(state => state[filterStateName]);
    const showCompleted = useSelector((state) => state[filterStateName].CanSeeCompleted);
    const showHidden = useSelector((state) => state[filterStateName].CanSeeHidden);
    const showAllSubtasks = useSelector((state) => state.showAllSubtasks);
    const showCalendarPageSetting = useSelector((state) => state.showCalendarPage);
    useEffect(() => {
        const loadTheme = async () => {
            try {
                const newTheme = await getTheme();
                setThemePage(newTheme);
            } catch (error) {
                console.error('Failed to load redirect URL:', error);
            }
        };

        loadTheme();
    }, []);
    const handleLicenseExpired = () => {
        window.open('https://support.texthelp.com/help/licence-expired', '_blank', 'noopener,noreferrer');
    };

    const handleHelpClick = () => {
        setShowProfileMenu(false);
        void openGlobalTasksHelp();
    };

    const handleFilterToggle = () => {
        setShowFilters(!showFilters);
    };

    const handleHiddenToggle = () => {
        dispatch(createFilterCanSeeAction(getfilterSetCanSeeHiddenActionType(feature), !showHidden));
    }

    const handleCompletedToggle = () => {
        dispatch(createFilterCanSeeAction(getfilterSetCanSeeCompletedActionType(feature), !showCompleted));
    }

    const showCalendarPage = () => {
        // If the setting is disabled, don't show calendar
        if (!showCalendarPageSetting) {
            return false;
        }

        // If on a native platform (mobile app), don't show calendar
        if (isMobile) {
            return false;
        }

        return true;
    };

    const handleToggle = () => {
        dispatch({ type: "TOGGLE_SHOW_ALL_SUBTASKS" });
    };

    const handleImportMindMapFileSelecter = () => {
        fileInputRef.current.click();
    };

    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        if (file) {

            const allowedExtensions = ['.mvdx', '.mvd', '.inspmaps'];


            const fileName = file.name.toLowerCase();
            const fileExtension = fileName.substring(fileName.lastIndexOf('.'));


            if (!allowedExtensions.includes(fileExtension)) {
                Toast.show({
                    icon: <svg xmlns="http://www.w3.org/2000/svg" width="16"
                        height="16"
                        viewBox="0 0 16 16">
                        <use href="#globaltasks-svg-error" />
                    </svg>,
                    description: `Invalid file type. Please select a mind map file with one of these extensions: ${allowedExtensions.join(', ')}`,
                    showClose: true,
                    duration: 5000
                });
                event.target.value = '';
                return;
            }

            const allowedMimeTypes = [
                'application/octet-stream',
                'application/x-inspmaps'
            ];

            if (file.type && !allowedMimeTypes.includes(file.type) && file.type !== '') {
                console.warn(`Unexpected MIME type: ${file.type} for file: ${fileName}`);
            }

            try {
                await dispatch(importMindMap(file));
            } catch (error) {
                console.error('Error importing mind map:', error);
                alert('An error occurred while importing the mind map. Please try again or contact support.');
            }
        } else {
            console.log('No file selected');
        }
        event.target.value = '';
    };

    const notify = () => {
        axios.post(`${import.meta.env.VITE_PUBLIC_DOMAIN}notification/notify`, {}, {
            headers: {
                'Content-Type': 'application/json'
            }
        })
            .then(response => {
                if (response.status !== 200) {
                    throw new Error('Failed to send notification');
                }
            })
            .catch(error => {
                console.error('Failed to send notification', error);
            });
    };

    const toggleProfileContextMenu = () => {
        setShowProfileMenu((prevState) => !prevState);
    };

    const navigateToPage = (destinationPage) => {
        if (location.pathname !== destinationPage) {
            navigate(destinationPage);
        }
    };

    useOutside(filterMenuRef, null, null, () => {
        setShowFilters(false);
    });

    useOutside(
        contextMenuRef,
        profileButtonRef,
        userPhotoRef,
        () => {
            setShowProfileMenu(false);
        },
        [userNameRef, userIconRef]
    );

    useEffect(() => {
        const fetchData = async () => {
            const data = JSON.parse(
                await getItem(
                    `firebase:user:${import.meta.env.VITE_PUBLIC_APP_ENV}`
                )
            );
            if (data) {
                try {
                    setUserName(data.name);
                } catch (error) {
                    console.error("Error fetching user info:", error);
                }
            }
        };
        fetchData();

    }, [photoUrl]);
    const handleSearchTextChange = (e) => {
        setSearchText(e.target.value);
    };
    const handleSearch = () => {
        const now = new Date();
        const trimmedSearchText = searchText?.trim();
        const searchState = { ...filterState, Text: trimmedSearchText };
        const filteredOverview = filterOverview(projects, filterState, new Date());
        const filteredTodos = getTimelineTodoList([...filteredOverview.projects, ...filteredOverview.tasks]);
        const filteredResults = filterSearchTodoList(filteredTodos, searchState, now);
        dispatch(setFilteredSearchResults({ ...filteredResults, Text: trimmedSearchText }));

        if (!location.pathname.includes("Search")) {
            navigate("/Search");
        }
    };
    const handleSearchPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const showMindMapButton = () => {
        return !Capacitor.isNativePlatform();
    };

    const isNativeApp = () => {
        return Capacitor.isNativePlatform();
        //return true
    };

    const syncEvents = () => {
        console.log('syncing events');
        setIsSyncing(true);
        dispatch(triggerCalendarSync());
        setTimeout(() => {
            setIsSyncing(false);
        }, 3000);
    };

    return (
        <header className="gt-header">

            <div className="gt-header__logo">
                <svg className="gt-logo" xmlns="http://www.w3.org/2000/svg">
                    <use xlinkHref="#globaltasks-svg-logo-icon"></use>
                    <use xlinkHref={theme ? theme === 'light' ? '#globaltasks-svg-logo' : '#globaltasks-svg-logo-dark' : '#globaltasks-svg-logo'}></use>
                </svg>
            </div>

            <button className="gt-header__filter" onClick={() => { handleFilterToggle(); }}>
                <svg xmlns="http://www.w3.org/2000/svg">
                    <use xlinkHref="#globaltasks-svg-filter"></use>
                </svg>
                Filters
            </button>
            {showFilters && (
                <div className={"gt-context-menu-activator gt-context-menu-activator--bottom-left"}>
                    <ul
                        className="gt-context-menu"
                        role="menu"
                        aria-orientation="vertical"
                        ref={filterMenuRef}
                    >
                        <li className="gt-context-menu__item" role="menuitem">
                            <div className="gw-group gw-group--space-between">
                                <p>Completed</p>
                                <div className="gw-toggle-checkbox">
                                    <input
                                        type="checkbox"
                                        className="gw-toggle-checkbox__input"
                                        id="toggle-show-completed"
                                        aria-label="Show Completed Tasks"
                                        onChange={handleCompletedToggle}
                                        checked={showCompleted}
                                        style={{ width: 'auto' }}
                                    />
                                </div>
                            </div>
                        </li>
                        <li className="gt-context-menu__item" role="menuitem">
                            <div className="gw-group gw-group--space-between">
                                <p>Hidden</p>
                                <div className="gw-toggle-checkbox">
                                    <input
                                        type="checkbox"
                                        className="gw-toggle-checkbox__input"
                                        id="toggle-show-hidden-tasks"
                                        aria-label="Show Hidden Tasks"
                                        onChange={handleHiddenToggle}
                                        checked={showHidden}
                                        style={{ width: 'auto' }}
                                    />
                                </div>
                            </div>
                        </li>
                        <li className="gt-context-menu__item" role="menuitem">
                            <div className="gw-group gw-group--space-between">
                                <p>Expand tasks</p>
                                <div className="gw-toggle-checkbox">
                                    <input
                                        type="checkbox"
                                        id="toggle-show-subtasks"
                                        aria-label="expand tasks"
                                        className="gw-toggle-checkbox__input"
                                        onChange={handleToggle}
                                        checked={showAllSubtasks}
                                        style={{ width: 'auto' }}
                                    />
                                </div>
                            </div>
                        </li>
                    </ul>
                </div>
            )}
            <div className="gt-nav">
                <button
                    className={`gt-nav__button ${location.pathname.toLowerCase().trim() === "/overview" ? "active" : ""
                        }`}
                    onClick={() => {
                        navigateToPage("/Overview");
                    }}
                >
                    <svg viewBox="0 0 19 19" xmlns="http://www.w3.org/2000/svg">
                        <use xlinkHref="#globaltasks-svg-home"></use>
                    </svg>
                    Overview
                </button>
                {showCalendarPage() && (
                    <button
                        className={`gt-nav__button ${location.pathname.toLowerCase().trim() === "/calendar" ? "active" : ""
                            }`}
                        onClick={() => {
                            navigateToPage("/Calendar");
                        }}
                    >
                        <svg viewBox="0 0 19 19" xmlns="http://www.w3.org/2000/svg">
                            <use xlinkHref="#globaltasks-svg-full-calendar"></use>
                        </svg>
                        Calendar
                    </button>
                )}
                <button
                    className={`gt-nav__button ${location.pathname.toLowerCase().trim() === "/todo" ? "active" : ""
                        }`}
                    onClick={() => {
                        navigateToPage("/Todo");
                    }}
                >
                    <svg viewBox="0 0 15 19" xmlns="http://www.w3.org/2000/svg">
                        <use xlinkHref="#globaltasks-svg-todo"></use>
                    </svg>
                    To-do
                </button>
                <button
                    className={`gt-nav__button ${location.pathname.toLowerCase().trim() === "/due-dates" ? "active" : ""
                        }`}
                    onClick={() => {
                        navigateToPage("/Due-dates");
                    }}
                >
                    <svg viewBox="0 0 19 19" xmlns="http://www.w3.org/2000/svg">
                        <use xlinkHref="#globaltasks-svg-due-date"></use>
                    </svg>
                    Due Dates
                </button>
                {isNativeApp() && location.pathname.toLowerCase().trim() === "/calendar" && (
                    <button
                        id="sync-btn"
                        onClick={syncEvents}
                        disabled={isSyncing}
                        className="gt-sync-btn"
                    >
                        <div className="gt-sync-btn__icon-wrapper">
                            <svg
                                id="sync-btn-icon"
                                className={`gt-sync-btn__icon ${isSyncing ? 'gt-sync-btn__icon--spinning' : ''}`}
                                width="20"
                                height="15"
                                viewBox="0 0 16 12"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path d="M11.0241 5.26137L13.0242 7.26145C13.1356 7.37287 13.2828 7.42859 13.4285 7.42859C13.5742 7.42859 13.7214 7.37287 13.8328 7.26145L15.8329 5.26137C16.0557 5.0385 16.0557 4.67709 15.8329 4.4528C15.61 4.22993 15.2471 4.22993 15.0243 4.4528L13.9771 5.49994C13.7229 2.4243 11.1399 0 7.99994 0C6.18854 0 4.49423 0.8057 3.34846 2.21C3.14845 2.45427 3.1856 2.81428 3.42988 3.01427C3.67416 3.21283 4.03417 3.17713 4.23416 2.93284C5.16129 1.7957 6.53412 1.14285 7.99989 1.14285C10.4898 1.14285 12.5484 3.02718 12.8242 5.44417L11.8328 4.45274C11.6099 4.22988 11.247 4.22988 11.0242 4.45274C10.8013 4.67561 10.8013 5.0385 11.0241 5.26137ZM0.167073 6.7385L2.16715 4.73842C2.39002 4.51555 2.75286 4.51555 2.97572 4.73842L4.9758 6.7385C5.19866 6.96136 5.19866 7.32278 4.9758 7.54707C4.75293 7.76993 4.39009 7.76993 4.16723 7.54707L3.1758 6.55564C3.45151 8.97281 5.51014 10.857 8.00009 10.857C9.46579 10.857 10.8386 10.2041 11.7658 9.06697C11.9658 8.82269 12.3258 8.78553 12.5701 8.98554C12.8144 9.18555 12.8515 9.54553 12.6515 9.78981C11.5058 11.1941 9.81159 11.9998 8.00004 11.9998C4.86003 11.9998 2.2772 9.5755 2.02284 6.49987L0.9757 7.54701C0.864272 7.65844 0.718569 7.71415 0.571424 7.71415C0.42428 7.71415 0.278562 7.65844 0.167148 7.54701C-0.0557164 7.32415 -0.0557917 6.96136 0.167073 6.7385Z" fill="white" />
                            </svg>
                        </div>
                        <span className="gt-sync-btn__text">
                            {isSyncing ? 'Syncing...' : 'Sync events'}
                        </span>
                    </button>
                )}
            </div>

            {location.pathname !== '/Calendar' ? (
                <div className="gt-header__search">
                    <button
                        className="gw-icon-button gw-icon-button--icon gw-icon-button--s2"
                        aria-label="search"
                        onClick={() => { navigate('/Search'); }}
                    >
                        <i className="gw-icon-button__icon gw-button__icon--search gw-icon-button__icon--s2"></i>
                    </button>
                    <div className="gt-header__search-field">
                        <input type="text" placeholder="Search" className="gw-input" onChange={handleSearchTextChange} onKeyDown={handleSearchPress} />
                        <button
                            className="gw-icon-button gw-icon-button--icon gw-icon-button--s2"
                            aria-label="search"
                            onClick={handleSearch}
                        >
                            <i className="gw-icon-button__icon gw-button__icon--search gw-icon-button__icon--s2"></i>
                        </button>
                    </div>
                </div>
            ) : (<div className="gt-header__search"></div>)
            }

            {(countdown <= 90) && (
                <div className="gt-license-warning" style={{ marginLeft: '2em' }}>
                    License expires in {countdown} days - <a href="#" onClick={() => { handleLicenseExpired(); }}
                    >renew license</a>
                </div>)}
            <div className="gt-header__dropdown">
                <button
                    className="gt-header__profile"
                    ref={profileButtonRef}
                    onClick={() => {
                        toggleProfileContextMenu();
                    }}
                >
                    <p
                        ref={userNameRef}
                        style={{ marginRight: '0.5em' }}
                    >
                        {userName ? userName : "Profile Name"}
                    </p>
                    {photoUrl ? (
                        <img
                            src={photoUrl}
                            height="40"
                            width="40"
                            style={{ borderRadius: '50%' }}
                        />
                    ) : (
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            ref={userPhotoRef}
                            onClick={(event) => {
                                event.stopPropagation();
                                toggleProfileContextMenu();
                            }}
                        >
                            <use
                                xlinkHref="#globaltasks-svg-profile"
                                ref={userIconRef}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    toggleProfileContextMenu();
                                }}
                            ></use>
                        </svg>)}
                </button>
                {showProfileMenu && (
                    <ul
                        className="gt-context-menu profile-context-menu"
                        role="menu"
                        aria-orientation="vertical"
                        ref={contextMenuRef}
                    >
                        {isMobile && (<li className="gt-context-menu__item" role="menuitem">
                            <button
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 14.516 14.516"
                                >
                                    <use xlinkHref="#globaltasks-svg-profile"></use>
                                </svg>
                                {userName}
                            </button>
                        </li>)}
                        <li className="gt-context-menu__item" role="menuitem">
                            <button
                                onClick={() => {
                                    openSettingsModal();
                                }}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 14.516 14.516"
                                >
                                    <use xlinkHref="#globaltasks-svg-settings"></use>
                                </svg>
                                Settings
                            </button>
                        </li>
                        {showMindMapButton() && (
                            <li className="gt-context-menu__item" role="menuitem">
                                <button
                                    onClick={() => { handleImportMindMapFileSelecter(); }}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 15.274 14.433"
                                    >
                                        <use xlinkHref="#globaltasks-svg-import"></use>
                                    </svg>
                                    Import Mind Map
                                </button>
                                <input type="file" accept=".mvdx, .mvd, .inspmaps" ref={fileInputRef}
                                    hidden onChange={handleFileChange} />
                            </li>
                        )}
                        {/*<li className="gt-context-menu__item" role="menuitem">*/}
                        {/*    <button*/}
                        {/*        onClick={() => { handlePrivacyPolicy(); }}*/}
                        {/*    >*/}
                        {/*        <svg*/}
                        {/*            xmlns="http://www.w3.org/2000/svg"*/}
                        {/*            viewBox="0 0 15.274 14.433"*/}
                        {/*        >*/}
                        {/*            <use xlinkHref="#globaltasks-svg-privacy-policy"></use>*/}
                        {/*        </svg>*/}
                        {/*        Privacy Policy*/}
                        {/*    </button>*/}
                        {/*</li>*/}
                        {/*<li className="gt-context-menu__item" role="menuitem">*/}
                        {/*    <button*/}
                        {/*        onClick={() => { handleTandCPolicy(); }}*/}
                        {/*    >*/}
                        {/*        <svg*/}
                        {/*            xmlns="http://www.w3.org/2000/svg"*/}
                        {/*            viewBox="0 0 15.274 14.433"*/}
                        {/*        >*/}
                        {/*            <use xlinkHref="#globaltasks-svg-t-and-c"></use>*/}
                        {/*        </svg>*/}
                        {/*        Terms & Conditions*/}
                        {/*    </button>*/}
                        {/*</li>*/}
                        {showHelp && (
                            <li className="gt-context-menu__item" role="menuitem">
                                <button type="button" onClick={handleHelpClick}>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 18 18"
                                        aria-hidden="true"
                                    >
                                        <use xlinkHref="#globaltasks-svg-info"></use>
                                    </svg>
                                    Help
                                </button>
                            </li>
                        )}
                        <li className="gt-context-menu__item" role="menuitem">
                            <button
                                onClick={async () => {
                                    await signOut();
                                }}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 15.274 14.433"
                                >
                                    <use xlinkHref="#globaltasks-svg-sign-out"></use>
                                </svg>
                                Sign Out
                            </button>
                        </li>
                    </ul>
                )}
            </div>
        </header>
    );

};

export default NavComponent;
